import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';
import { BonusProgram, Campaign, ReportGroup, ReportOverview, ReportRow, errText, money } from './bonusTypes';

interface Props {
  programs: BonusProgram[];
  campaigns: Campaign[];
  /** Jump to the tab where the thing can be dealt with. */
  onGo: (tab: string) => void;
}

const GROUPS: Array<{ value: ReportGroup; label: string }> = [
  { value: 'program', label: 'Program' }, { value: 'campaign', label: 'Campaign' }, { value: 'employee', label: 'Employee' }, { value: 'month', label: 'Month' }, { value: 'week', label: 'Week' },
];

const perCurrency = (m: Record<string, number>) => Object.entries(m).map(([c, v]) => money(v, c)).join(' · ') || 'None';

const ReportsPanel: React.FC<Props> = ({ programs, campaigns, onGo }) => {
  const [groupBy, setGroupBy] = useState<ReportGroup>('program');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [programId, setProgramId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [totals, setTotals] = useState<ReportRow[]>([]);
  const [overview, setOverview] = useState<ReportOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const params = useCallback(() => ({ groupBy, from: from || undefined, to: to || undefined, programId: programId || undefined, campaignId: campaignId || undefined }), [groupBy, from, to, programId, campaignId]);

  const load = useCallback(async () => {
    try {
      const [r, o] = await Promise.all([bonusTrackerAPI.reportSummary(params()), bonusTrackerAPI.reportOverview()]);
      setRows(r.rows);
      setTotals(r.totals);
      setOverview(o);
      setError(null);
    } catch (e) { setError(errText(e)); }
  }, [params]);
  useEffect(() => { void load(); }, [load]);

  const exportCsv = async () => {
    try {
      const blob = await bonusTrackerAPI.reportExport(params());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bonus-report-${groupBy}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(errText(e)); }
  };

  const cards: Array<{ label: string; value: string; note?: string; tab: string; warn?: boolean }> = overview ? [
    { label: 'Waiting for payroll to pay', value: String(overview.waitingForPayroll.count), note: overview.waitingForPayroll.count ? `oldest ${overview.waitingForPayroll.oldestDays} day(s)` : undefined, tab: 'payroll' },
    { label: 'Payroll check problems', value: String(overview.payrollCheckProblems), note: 'amount differs, missing or cancelled', tab: 'payroll', warn: overview.payrollCheckProblems > 0 },
    { label: 'Customer still owes', value: perCurrency(overview.customerStillOwes), tab: 'funding' },
    { label: 'Customer payments not matched', value: perCurrency(overview.customerPaymentsNotMatched), tab: 'funding' },
    { label: 'Eligibility reviews open', value: String(overview.eligibilityUnderReview), note: `${overview.earningsHeldForEligibility} earning(s) held`, tab: 'eligibility', warn: overview.eligibilityUnderReview > 0 },
    { label: 'Overpayments to recover', value: String(overview.overpaymentsToRecover.count), note: overview.overpaymentsToRecover.count ? money(overview.overpaymentsToRecover.total) : undefined, tab: 'payroll', warn: overview.overpaymentsToRecover.count > 0 },
    { label: 'Old sheet rows to classify', value: String(overview.pendingReconciliation.rows), note: overview.pendingReconciliation.rows ? money(overview.pendingReconciliation.total) : undefined, tab: 'history', warn: overview.pendingReconciliation.rows > 0 },
  ] : [];

  const groupLabel = GROUPS.find((g) => g.value === groupBy)?.label || '';

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Totals of the earnings in the bonuses, per currency (currencies are never added together). <b>Sent to payroll is not paid</b>: only what payroll settled counts as paid.
        Rejected earnings are shown apart and are not in the total. Reports only read: nothing here changes a bonus, an approval or a payment.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
        {cards.map((c) => (
          <Card key={c.label} variant="outlined" sx={c.warn ? { borderColor: 'warning.main' } : undefined}>
            <CardActionArea onClick={() => onGo(c.tab)}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{c.value}</Typography>
                {c.note && <Typography variant="caption" color="text.secondary">{c.note}</Typography>}
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Group by</InputLabel>
          <Select label="Group by" value={groupBy} onChange={(e) => setGroupBy(e.target.value as ReportGroup)}>
            {GROUPS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" type="date" label="Period ends from" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
        <TextField size="small" type="date" label="to" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Program</InputLabel>
          <Select label="Program" value={programId} onChange={(e) => setProgramId(e.target.value)}>
            <MenuItem value="">All programs</MenuItem>
            {programs.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Campaign</InputLabel>
          <Select label="Campaign" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
            <MenuItem value="">All campaigns</MenuItem>
            {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={() => void exportCsv()} sx={{ ml: { md: 'auto !important' } }}>Export CSV</Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{groupLabel}</TableCell>
              <TableCell align="right">Earnings</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Approved</TableCell>
              <TableCell align="right">In approval</TableCell>
              <TableCell align="right">Ready for payroll</TableCell>
              <TableCell align="right">Sent, not paid</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Customer still owes</TableCell>
              <TableCell align="right">Held (eligibility)</TableCell>
              <TableCell align="right">Problems</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.key}|${r.currency}`} hover>
                <TableCell>{r.label}<Typography variant="caption" display="block" color="text.secondary">{r.employees} employee(s) · {r.currency}{r.rejected > 0 ? ` · ${money(r.rejected, r.currency)} rejected` : ''}</Typography></TableCell>
                <TableCell align="right">{r.earnings}</TableCell>
                <TableCell align="right">{money(r.total, r.currency)}</TableCell>
                <TableCell align="right">{money(r.approved, r.currency)}</TableCell>
                <TableCell align="right">{money(r.inApproval, r.currency)}</TableCell>
                <TableCell align="right">{money(r.readyForPayroll, r.currency)}</TableCell>
                <TableCell align="right">{money(r.sentNotPaid, r.currency)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{money(r.paid, r.currency)}</TableCell>
                <TableCell align="right">{r.customerDue > 0 ? money(r.customerOutstanding, r.currency) : '-'}</TableCell>
                <TableCell align="right">{r.heldCount ? `${r.heldCount} · ${money(r.heldAmount, r.currency)}` : '-'}</TableCell>
                <TableCell align="right" sx={r.problems ? { color: 'error.main', fontWeight: 700 } : undefined}>{r.problems || '-'}</TableCell>
              </TableRow>
            ))}
            {totals.map((t) => (
              <TableRow key={`total-${t.currency}`} sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                <TableCell>Total {t.currency}</TableCell>
                <TableCell align="right">{t.earnings}</TableCell>
                <TableCell align="right">{money(t.total, t.currency)}</TableCell>
                <TableCell align="right">{money(t.approved, t.currency)}</TableCell>
                <TableCell align="right">{money(t.inApproval, t.currency)}</TableCell>
                <TableCell align="right">{money(t.readyForPayroll, t.currency)}</TableCell>
                <TableCell align="right">{money(t.sentNotPaid, t.currency)}</TableCell>
                <TableCell align="right">{money(t.paid, t.currency)}</TableCell>
                <TableCell align="right">{t.customerDue > 0 ? money(t.customerOutstanding, t.currency) : '-'}</TableCell>
                <TableCell align="right">{t.heldCount ? `${t.heldCount} · ${money(t.heldAmount, t.currency)}` : '-'}</TableCell>
                <TableCell align="right">{t.problems || '-'}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={11}><Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No earnings match these filters.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        "Customer still owes" is each earning's proportional share of what customers paid for its bonus (payments are matched to whole bonuses, so by employee it is an estimate).
        "Problems" are earnings whose payroll check did not match.
      </Typography>
    </Box>
  );
};

export default ReportsPanel;
